import React from 'react';
import { MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ViewType } from '../hooks/useViewState';
import type { IapScope } from '../ICSConstants';
import type { ActionColumnId } from './actionColumnModel';
import { ActionColumnMenuItems } from './ActionColumnMenuItems';

const VIEW_OPTIONS: { label: string; value: ViewType }[] = [
  { label: 'List', value: 'table' },
  { label: 'Board', value: 'kanban' },
  { label: 'Timeline', value: 'timeline' },
];

function isViewType(value: string): value is ViewType {
  return value === 'table' || value === 'kanban' || value === 'timeline';
}

type ActionsViewMenuColumns = {
  scope: IapScope;
  hiddenColumns: readonly string[];
  onToggleColumn: (id: ActionColumnId) => void;
  onResetColumns: () => void;
};

/** One layout at a time. Used by the incident Tasks page and Planning Phase. */
type ActionsViewMenuRadioProps = ActionsViewMenuColumns & {
  view: ViewType;
  onViewChange: (view: ViewType) => void;
  selectedViews?: never;
  onSelectedViewsChange?: never;
};

/**
 * Several layouts at once. Unchecking the last one is ignored, so the list
 * never disappears entirely.
 */
type ActionsViewMenuCheckboxProps = ActionsViewMenuColumns & {
  selectedViews: readonly ViewType[];
  onSelectedViewsChange: (views: ViewType[]) => void;
  view?: never;
  onViewChange?: never;
};

type ActionsViewMenuProps =
  | ActionsViewMenuRadioProps
  | ActionsViewMenuCheckboxProps;

function nextSelectedViews(
  current: readonly ViewType[],
  view: ViewType,
  checked: boolean
): ViewType[] | null {
  if (!checked) {
    if (current.length <= 1) return null;
    return current.filter((item) => item !== view);
  }
  return current.includes(view) ? [...current] : [...current, view];
}

/** Kebab for layout radios and Actions-table column checkboxes. */
export const ActionsViewMenu: React.FC<ActionsViewMenuProps> = ({
  scope,
  hiddenColumns,
  onToggleColumn,
  onResetColumns,
  ...layout
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            aria-label="Task view options"
            data-tour="hwcg-tasks-view"
          />
        }
      >
        <MoreVertical className="tw:h-4 tw:w-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="dark tw:w-52">
        <DropdownMenuLabel>Layout</DropdownMenuLabel>
        {layout.selectedViews ? (
          VIEW_OPTIONS.map((option) => (
            <DropdownMenuCheckboxItem
              key={option.value}
              checked={layout.selectedViews.includes(option.value)}
              onCheckedChange={(checked) => {
                const next = nextSelectedViews(
                  layout.selectedViews,
                  option.value,
                  checked === true
                );
                if (next) layout.onSelectedViewsChange(next);
              }}
            >
              {option.label}
            </DropdownMenuCheckboxItem>
          ))
        ) : (
          <DropdownMenuRadioGroup
            value={layout.view}
            onValueChange={(next) => {
              if (isViewType(next)) layout.onViewChange(next);
            }}
          >
            {VIEW_OPTIONS.map((option) => (
              <DropdownMenuRadioItem key={option.value} value={option.value}>
                {option.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuLabel>Columns</DropdownMenuLabel>
          <ActionColumnMenuItems
            scope={scope}
            hidden={hiddenColumns}
            onToggle={onToggleColumn}
            onReset={onResetColumns}
          />
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
